import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Clinic, Doctor } from '@/types/models';

interface ContextState {
  // State
  clinicId: string | null;
  doctorId: string | null;
  clinic: Clinic | null;
  doctor: Doctor | null;
  isHydrated: boolean;

  // Actions
  setClinic: (clinic: Clinic | null) => void;
  setDoctor: (doctor: Doctor | null) => void;
  clearContext: () => void;
  clearDoctor: () => void;
  setHydrated: (hydrated: boolean) => void;
  hydrateFromIds: (clinic: Clinic | null, doctor: Doctor | null) => void;

  // Computed
  hasContext: boolean;
  hasFullContext: boolean;
}

export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      // Initial state
      clinicId: null,
      doctorId: null,
      clinic: null,
      doctor: null,
      isHydrated: false,

      // Computed getters
      get hasContext() {
        const state = get();
        return !!state.clinic || !!state.clinicId;
      },

      get hasFullContext() {
        const state = get();
        return (!!state.clinic || !!state.clinicId) && (!!state.doctor || !!state.doctorId);
      },

      // Actions
      setClinic: (clinic: Clinic | null) => {
        console.log('[Context Store] setClinic called:', clinic?.name);
        set({
          clinic,
          clinicId: clinic?.id || null,
          // Clear doctor when clinic changes
          doctor: null,
          doctorId: null,
          isHydrated: false
        });
      },

      setDoctor: (doctor: Doctor | null) => {
        console.log('[Context Store] setDoctor called:', doctor?.name);
        set({
          doctor,
          doctorId: doctor?.id || null
        });
      },

      clearContext: () => {
        set({
          clinicId: null,
          doctorId: null,
          clinic: null,
          doctor: null
        });
      },

      clearDoctor: () => {
        set({
          doctorId: null,
          doctor: null
        });
      },

      setHydrated: (hydrated: boolean) => {
        set({ isHydrated: hydrated });
      },

      hydrateFromIds: (clinic: Clinic | null, doctor: Doctor | null) => {
        console.log('[Context Store] hydrateFromIds called:', { 
          clinic: clinic?.name, 
          clinicId: clinic?.id,
          doctor: doctor?.name,
          doctorId: doctor?.id
        });
        set({
          clinic,
          clinicId: clinic?.id || null,
          doctor,
          doctorId: doctor?.id || null,
          isHydrated: true
        });
      }
    }),
    {
      name: 'active-context',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist IDs, not the full objects
        clinicId: state.clinicId,
        doctorId: state.doctorId
      }),
      onRehydrateStorage: () => {
        console.log('[Context Store] onRehydrateStorage - before rehydration');
        return (state) => {
          // When rehydrating from storage, mark as not hydrated yet
          // so that the useContextHydration hook can fetch the full objects
          console.log('[Context Store] onRehydrateStorage - after rehydration:', {
            clinicId: state?.clinicId,
            doctorId: state?.doctorId,
            hasState: !!state
          });
          if (state) {
            state.isHydrated = false;
          }
        };
      }
    }
  )
);